import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import {
  isPermissionRefused,
  type RoleAssignment,
  type RoleAssignmentWrite,
  type RoleAssignmentsResponse,
} from '@citibox-panel/api-client';
import { Alert, Box, Button, Dialog, Stack, Text } from '@citibox-panel/ui';
import type { Permission } from '@/lib/access';
import type { SaveRequest } from '../hooks/useSaveRoleAssignment';
import {
  assignableRoles,
  draftOf,
  isEmptyDraft,
  isReadOnly,
  offerableExtras,
  unmanaged,
  type AssignmentDraft,
} from '../lib/assignments';
import { hasChanges, summarize } from '../lib/changes';
import { canonicalEmail, isCitiboxEmail } from '../lib/email';
import { classifySaveError } from '../lib/errors';
import { AssignmentForm } from './AssignmentForm';
import { ConfirmStep } from './ConfirmStep';

// TODO: i18n
const COPY = {
  addTitle: 'Añadir persona',
  editTitle: (email: string) => `Editar ${email}`,
  emailInvalid: 'Escribe un email @citibox.com.',
  emailProtected: 'Las personas de Sistemas se gestionan desde backend.',
  emailExisting: 'Esta persona ya tiene asignación.',
  editExisting: 'Editarla',
  add: 'Añadir',
  save: 'Guardar',
  cancel: 'Cancelar',
  removeAll: 'Quitar todo',
  conflict: 'Esta persona ha cambiado desde que abriste el diálogo.',
  reloadCurrent: 'Cargar la versión actual',
  reloadFailed: 'No se ha podido cargar la versión actual. Vuelve a intentarlo.',
  upToDate: 'Ya está al día; no hay cambios que guardar.',
  gone: 'Otra persona de Sistemas le quitó el acceso.',
  readd: 'Volver a añadir',
  backendManaged: 'Esta persona se gestiona desde backend.',
  removeTitle: (email: string) => `¿Quitar el acceso a ${email}?`,
  removeLine: (email: string, market: string | null) =>
    `${email} perderá el acceso al panel en ${market ?? 'este entorno (sin confirmar)'}.`,
  removeAlso: (ids: readonly string[]) => `También se quitarán: ${ids.join(', ')}.`,
  removeUntilEnforced:
    'Los endpoints aún no comprueban los permisos: aunque deje de ver el panel, su cuenta @citibox.com puede seguir llamándolos.',
  removeConfirm: 'Quitar acceso',
} as const;

type Step = 'form' | 'confirm-remove' | 'conflict' | 'gone' | 'backend-managed';
type FocusTarget = 'remove' | 'summary';

const EMPTY_DRAFT: AssignmentDraft = { roles: [], extraPermissions: [] };
const NOTHING_UNMANAGED: ReturnType<typeof unmanaged> = { roles: [], extras: [] };

export interface AssignmentDialogProps {
  open: boolean;
  mode: 'add' | 'edit';
  // The person being edited, or null to add one.
  initial: RoleAssignment | null;
  // Pre-fills the add form when it is opened from a filter that matched nobody.
  initialEmail: string;
  catalog: RoleAssignmentsResponse;
  // The bare market («ES»), or null when the deployment cannot be trusted to name it.
  marketLabel: string | null;
  existingEmails: ReadonlySet<string>;
  // Read-only rows: people whose effective permissions include access management.
  protectedEmails: ReadonlySet<string>;
  selfEmail: string | null;
  save: (request: SaveRequest) => Promise<void>;
  pending: boolean;
  onReloadCurrent: (email: string) => Promise<RoleAssignment | null>;
  onClose: () => void;
  onSaved: (email: string) => void;
  onSwitchToEdit: (email: string) => void;
  // The dialog is really gone: until its exit transition ends, MUI keeps the rest of the app
  // aria-hidden, so the page moves focus only from here on.
  onExited?: () => void;
}

// One dialog for adding and editing, with its confirmations as steps inside it, so «Cancelar» on a
// confirmation returns to the form with every mark intact. The page mounts a fresh instance per
// opening, so the state below is seeded once from the props.
export function AssignmentDialog({
  open,
  mode: openedAs,
  initial,
  initialEmail,
  catalog,
  marketLabel,
  existingEmails,
  protectedEmails,
  selfEmail,
  save,
  pending,
  onReloadCurrent,
  onClose,
  onSaved,
  onSwitchToEdit,
  onExited,
}: AssignmentDialogProps): ReactElement {
  const [mode, setMode] = useState(openedAs);
  // The version the person's marks are compared against and the write is guarded by. A 409 reload
  // swaps it for the current one while the marks stay.
  const [baseline, setBaseline] = useState<RoleAssignment | null>(initial);
  const [email, setEmail] = useState(initial?.email ?? initialEmail);
  const [draft, setDraft] = useState<AssignmentDraft>(() =>
    initial === null ? EMPTY_DRAFT : draftOf(initial, catalog.roles, catalog.permissions),
  );
  const [step, setStep] = useState<Step>('form');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [existing, setExisting] = useState<string | null>(null);
  // Counts refused emails, so the same refusal twice still moves focus back to the field.
  const [emailRefusals, setEmailRefusals] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  // The baseline came from «Cargar la versión actual», so a summary with nothing in it is news.
  const [reloaded, setReloaded] = useState(false);
  const titleId = useId();
  const stepTitleId = useId();
  const removeRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const focusOnForm = useRef<FocusTarget | null>(null);

  // Back on the form, the keyboard lands where the step was opened from, or on the recomputed
  // summary after a reload, instead of on <body>. A reload that left nothing to change has no
  // summary and a disabled «Guardar», so the keyboard goes to the title, which is always there.
  useEffect(() => {
    if (step !== 'form') return;
    const target = focusOnForm.current;
    focusOnForm.current = null;
    if (target === 'remove') removeRef.current?.focus();
    if (target === 'summary') (summaryRef.current ?? titleRef.current)?.focus();
  }, [step, baseline]);

  // The error is the field's description, not a live region: once it is rendered, the keyboard
  // goes back to the field so a screen reader says it instead of nothing on «Añadir».
  useEffect(() => {
    if (emailRefusals > 0) emailRef.current?.focus();
  }, [emailRefusals]);

  const baselineDraft =
    baseline === null ? EMPTY_DRAFT : draftOf(baseline, catalog.roles, catalog.permissions);
  const summary = summarize(baselineDraft, draft);
  const roles = assignableRoles(catalog.roles);
  const extras = offerableExtras(catalog.permissions);
  const leftovers =
    baseline === null ? NOTHING_UNMANAGED : unmanaged(baseline, catalog.roles, catalog.permissions);
  // A refused email keeps «Añadir» off until the field changes: pressing it again would only say
  // the same thing.
  // A read-only baseline never reaches the form, but a save from it would drop what backend holds.
  const readOnlyBaseline = baseline !== null && isReadOnly(baseline, catalog.roles);
  const canSave =
    !readOnlyBaseline &&
    !isEmptyDraft(draft) &&
    hasChanges(summary) &&
    emailError === null &&
    !pending;
  const target = baseline?.email ?? canonicalEmail(email);
  const formTitle = mode === 'add' ? COPY.addTitle : COPY.editTitle(target);
  const upToDate = reloaded && mode === 'edit' && !hasChanges(summary);

  const includedBy = (permission: Permission): string | null =>
    roles.find((role) => draft.roles.includes(role.id) && role.permissions.includes(permission))
      ?.name ?? null;

  // Escape, the backdrop and «Cancelar» all come through here: a write in flight cannot be taken back.
  const handleClose = (): void => {
    if (pending || reloading) return;
    onClose();
  };

  // Reset once the dialog is really gone, never on close, so it does not blank during its exit.
  const handleExited = (): void => {
    setStep('form');
    setFailure(null);
    setReloaded(false);
    setEmailError(null);
    setExisting(null);
    onExited?.();
  };

  const changeEmail = (value: string): void => {
    setEmail(value);
    setEmailError(null);
    setExisting(null);
  };

  // Checked on submit only: an error while the person is still typing is noise.
  const acceptsNewEmail = (canonical: string): boolean => {
    if (!isCitiboxEmail(canonical)) {
      setEmailError(COPY.emailInvalid);
      return false;
    }
    const self = selfEmail === null ? null : canonicalEmail(selfEmail);
    if (canonical === self || protectedEmails.has(canonical)) {
      setEmailError(COPY.emailProtected);
      return false;
    }
    if (existingEmails.has(canonical)) {
      setEmailError(COPY.emailExisting);
      setExisting(canonical);
      return false;
    }
    return true;
  };

  // The roles that are marked: the server replaces the catalog roles with them and keeps the ones
  // outside the catalog. The extras are the whole set, so the ones shown read-only go back too, or
  // the server would remove them.
  const write = (): RoleAssignmentWrite => ({
    email: target,
    expectedVersion: baseline?.version ?? null,
    roles: [...draft.roles],
    extraPermissions: [...draft.extraPermissions, ...leftovers.extras],
  });

  const run = async (request: SaveRequest): Promise<void> => {
    setFailure(null);
    try {
      await save(request);
    } catch (error) {
      const outcome = classifySaveError(error);
      // The host replaces the view; nothing to say here.
      if (outcome.kind === 'denied') return;
      if (outcome.kind === 'conflict') {
        setStep('conflict');
        return;
      }
      setFailure(outcome.text);
      setStep('form');
      return;
    }
    onSaved(request.kind === 'upsert' ? request.write.email : request.email);
  };

  const submit = (): void => {
    setFailure(null);
    if (baseline === null && !acceptsNewEmail(target)) {
      setEmailRefusals((count) => count + 1);
      return;
    }
    void run({ kind: 'upsert', write: write() });
  };

  const reloadCurrent = async (): Promise<void> => {
    setReloading(true);
    setFailure(null);
    try {
      const fresh = await onReloadCurrent(target);
      if (fresh === null) {
        setStep('gone');
        return;
      }
      // The reload returns the row only, so it is judged against the catalog the dialog opened with.
      if (isReadOnly(fresh, catalog.roles)) {
        setBaseline(fresh);
        setStep('backend-managed');
        return;
      }
      focusOnForm.current = 'summary';
      setReloaded(true);
      setMode('edit');
      setBaseline(fresh);
      setEmail(fresh.email);
      setStep('form');
    } catch (error) {
      // A refusal is the host's to show, since it replaces the view; anything else is said here,
      // and the conflict stays.
      if (!isPermissionRefused(error)) setFailure(COPY.reloadFailed);
    } finally {
      setReloading(false);
    }
  };

  // The person was removed meanwhile: recreating them is a new add, guarded as one (no version).
  const readd = (): void => {
    setMode('add');
    setBaseline(null);
    setStep('form');
  };

  const confirming = step === 'confirm-remove';
  const stepTitle = COPY.removeTitle(target);
  const failureAlert = failure !== null && <Alert severity="error">{failure}</Alert>;

  const cancel = (
    <Button color="neutral" sentenceCase disabled={pending || reloading} onClick={handleClose}>
      {COPY.cancel}
    </Button>
  );

  const formActions = (
    <>
      {mode === 'edit' && (
        <Button
          ref={removeRef}
          variant="text"
          color="error"
          sentenceCase
          disabled={pending}
          onClick={() => {
            focusOnForm.current = 'remove';
            setFailure(null);
            setStep('confirm-remove');
          }}
        >
          {COPY.removeAll}
        </Button>
      )}
      {/* Pushes «Quitar todo» to the far left, away from «Guardar». */}
      <Box sx={{ flexGrow: 1 }} />
      {cancel}
      <Button
        variant="contained"
        sentenceCase
        loading={pending}
        disabled={!canSave}
        onClick={submit}
      >
        {mode === 'add' ? COPY.add : COPY.save}
      </Button>
    </>
  );

  let body: ReactElement;
  if (step === 'confirm-remove') {
    const leftoverIds = [...leftovers.roles.map((role) => role.id), ...leftovers.extras];
    body = (
      <ConfirmStep
        titleId={stepTitleId}
        title={COPY.removeTitle(target)}
        lines={[
          COPY.removeLine(target, marketLabel),
          ...(leftoverIds.length > 0 ? [COPY.removeAlso(leftoverIds)] : []),
          COPY.removeUntilEnforced,
        ]}
        confirmLabel={COPY.removeConfirm}
        pending={pending}
        onConfirm={() => {
          if (baseline !== null) {
            void run({
              kind: 'remove-all',
              email: baseline.email,
              expectedVersion: baseline.version,
            });
          }
        }}
        onCancel={() => {
          setStep('form');
        }}
      />
    );
  } else if (step === 'conflict') {
    body = (
      <Stack spacing={2}>
        <Alert
          severity="warning"
          action={
            <Button
              sentenceCase
              loading={reloading}
              onClick={() => {
                void reloadCurrent();
              }}
            >
              {COPY.reloadCurrent}
            </Button>
          }
        >
          {COPY.conflict}
        </Alert>
        {failureAlert}
      </Stack>
    );
  } else if (step === 'gone') {
    body = (
      <Alert
        severity="warning"
        action={
          <Button sentenceCase onClick={readd}>
            {COPY.readd}
          </Button>
        }
      >
        {COPY.gone}
      </Alert>
    );
  } else if (step === 'backend-managed') {
    body = <Alert severity="info">{COPY.backendManaged}</Alert>;
  } else {
    body = (
      <Stack spacing={2}>
        <AssignmentForm
          mode={mode}
          email={email}
          {...(mode === 'add' ? { onEmailChange: changeEmail } : {})}
          emailRef={emailRef}
          emailError={emailError}
          {...(existing !== null
            ? {
                emailErrorAction: (
                  <Box>
                    <Button
                      variant="inline"
                      onClick={() => {
                        onSwitchToEdit(existing);
                      }}
                    >
                      {COPY.editExisting}
                    </Button>
                  </Box>
                ),
              }
            : {})}
          roles={roles}
          extras={extras}
          draft={draft}
          onDraftChange={setDraft}
          includedBy={includedBy}
          unmanaged={leftovers}
          summary={mode === 'edit' ? summary : null}
          summaryRef={summaryRef}
        />
        {upToDate && <Alert severity="info">{COPY.upToDate}</Alert>}
        {failureAlert}
      </Stack>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onExited={handleExited}
      disableBackdropClose
      // The name follows the step, so a screen reader hears that the content changed.
      label={confirming ? stepTitle : formTitle}
      labelledBy={confirming ? stepTitleId : titleId}
      {...(confirming
        ? {}
        : {
            // Focusable from code only, for the reload that leaves nothing to save.
            title: (
              <Box component="h2" id={titleId} ref={titleRef} tabIndex={-1} sx={{ m: 0 }}>
                <Text component="span" variant="h6">
                  {formTitle}
                </Text>
              </Box>
            ),
          })}
      // A confirmation carries its own buttons; the conflict steps offer only «Cancelar».
      {...(step === 'form' ? { actions: formActions } : confirming ? {} : { actions: cancel })}
    >
      {body}
    </Dialog>
  );
}
